import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type {
  CrmClient,
  CrmSegment,
  CrmSortField,
  CrmSortOrder,
  CrmGroupBy,
  CrmSubFilter,
  CustomerStatsCacheItem,
} from '@/types/crm';

export interface CrmState {
  // State
  clients: CrmClient[];
  segments: CrmSegment[];
  activeSegmentId: string;
  activeSubFilter: CrmSubFilter;
  loading: boolean;
  loadingPhase: 'segments' | 'customers' | 'idle';
  loadedCount: number;
  totalCount: number;
  searchQuery: string;
  sortBy: CrmSortField;
  sortOrder: CrmSortOrder;
  groupBy: CrmGroupBy;
  hideProcessed: boolean;
  processedMap: Record<string, string>;
  segmentMemberships: Record<string, string[]>;
  statsCache: Record<string, CustomerStatsCacheItem>;
  statsProgress: { current: number; total: number };

  // Actions
  setClients: (clients: CrmClient[]) => void;
  updateClientsBatch: (updatesMap: Record<string, Partial<CrmClient>>) => void;
  setSegments: (segments: CrmSegment[]) => void;
  setActiveSegmentId: (id: string) => void;
  setActiveSubFilter: (subFilter: CrmSubFilter) => void;
  setSegmentMemberships: (memberships: Record<string, string[]>) => void;
  setLoading: (loading: boolean) => void;
  setLoadingProgress: (phase: 'segments' | 'customers' | 'idle', loaded: number, total: number) => void;
  setSearchQuery: (query: string) => void;
  setSortBy: (sortBy: CrmSortField) => void;
  setSortOrder: (sortOrder: CrmSortOrder) => void;
  setGroupBy: (groupBy: CrmGroupBy) => void;
  setHideProcessed: (hide: boolean) => void;
  markProcessed: (id: string) => void;
  unmarkProcessed: (id: string) => void;
  isProcessed: (id: string) => boolean;
  updateStatsCache: (id: string, item: CustomerStatsCacheItem) => void;
  setStatsCacheBatch: (batch: Record<string, CustomerStatsCacheItem>) => void;
  setStatsProgress: (current: number, total: number) => void;
}

export const useCrmStore = create<CrmState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial State
        clients: [],
        segments: [],
        activeSegmentId: '',
        activeSubFilter: 'all',
        loading: false,
        loadingPhase: 'idle',
        loadedCount: 0,
        totalCount: 0,
        searchQuery: '',
        sortBy: 'name',
        sortOrder: 'asc',
        groupBy: 'none',
        hideProcessed: false,
        processedMap: {},
        segmentMemberships: {},
        statsCache: {},
        statsProgress: { current: 0, total: 0 },

        // Actions
        setClients: (clients) => {
          set((state) => ({
            clients: clients.map((c) => ({
              ...c,
              isProcessed: Boolean(
                state.processedMap[c.id] || state.processedMap[c.customerId] || c.isProcessed
              ),
            })),
          }));
        },

        updateClientsBatch: (updatesMap) => {
          set((state) => ({
            clients: state.clients.map((c) => {
              const u = updatesMap[c.id] || updatesMap[c.customerId];
              return u ? { ...c, ...u } : c;
            }),
          }));
        },

        setSegments: (segments) => set({ segments }),

        setActiveSegmentId: (activeSegmentId) => set({ activeSegmentId }),

        setActiveSubFilter: (activeSubFilter) => set({ activeSubFilter }),

        setSegmentMemberships: (segmentMemberships) => set({ segmentMemberships }),

        setLoading: (loading) => set({ loading }),

        setLoadingProgress: (loadingPhase, loadedCount, totalCount) =>
          set({ loadingPhase, loadedCount, totalCount }),

        setSearchQuery: (searchQuery) => set({ searchQuery }),

        setSortBy: (sortBy) => set({ sortBy }),

        setSortOrder: (sortOrder) => set({ sortOrder }),

        setGroupBy: (groupBy) => set({ groupBy }),

        setHideProcessed: (hideProcessed) => set({ hideProcessed }),

        markProcessed: (id) => {
          set((state) => {
            const timestamp = new Date().toISOString();
            const newMap = { ...state.processedMap, [id]: timestamp };
            return {
              processedMap: newMap,
              clients: state.clients.map((c) =>
                c.id === id || c.customerId === id ? { ...c, isProcessed: true } : c
              ),
            };
          });
        },

        unmarkProcessed: (id) => {
          set((state) => {
            const newMap = { ...state.processedMap };
            delete newMap[id];
            return {
              processedMap: newMap,
              clients: state.clients.map((c) =>
                c.id === id || c.customerId === id ? { ...c, isProcessed: false } : c
              ),
            };
          });
        },

        isProcessed: (id) => {
          return Boolean(get().processedMap[id]);
        },

        updateStatsCache: (id, item) => {
          set((state) => ({
            statsCache: {
              ...state.statsCache,
              [id]: item,
            },
          }));
        },

        setStatsCacheBatch: (batch) => {
          set((state) => ({
            statsCache: {
              ...state.statsCache,
              ...batch,
            },
          }));
        },

        setStatsProgress: (current, total) =>
          set({ statsProgress: { current, total } }),
      }),
      {
        name: 'fresha-crm-v2',
        partialize: (state) => ({
          activeSegmentId: state.activeSegmentId,
          activeSubFilter: state.activeSubFilter,
          processedMap: state.processedMap,
          sortBy: state.sortBy,
          sortOrder: state.sortOrder,
          groupBy: state.groupBy,
          hideProcessed: state.hideProcessed,
        }),
      }
    ),
    { name: 'CrmStore' }
  )
);
